/* Video timestamps (YouTube + Vimeo) - safe loaders, queued seeks, duration auto-calc */
(function () {
  function loadYouTubeAPI(cb) {
    if (window.YT && window.YT.Player) return cb();

    window.__aiYtReadyCbs = window.__aiYtReadyCbs || [];
    window.__aiYtReadyCbs.push(cb);

    if (window.__aiYtScriptLoading) return;
    window.__aiYtScriptLoading = true;

    var prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = function () {
      if (typeof prev === "function") prev();
      var cbs = window.__aiYtReadyCbs || [];
      window.__aiYtReadyCbs = [];
      cbs.forEach(function (fn) {
        try { fn(); } catch (e) {}
      });
    };

    var tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    var firstScriptTag = document.getElementsByTagName("script")[0];
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
  }

  function loadVimeoAPI(cb) {
    if (window.Vimeo && window.Vimeo.Player) return cb();

    window.__aiVimeoReadyCbs = window.__aiVimeoReadyCbs || [];
    window.__aiVimeoReadyCbs.push(cb);

    if (window.__aiVimeoScriptLoading) return;
    window.__aiVimeoScriptLoading = true;

    var script = document.createElement("script");
    script.src = "https://player.vimeo.com/api/player.js";
    script.onload = function () {
      var cbs = window.__aiVimeoReadyCbs || [];
      window.__aiVimeoReadyCbs = [];
      cbs.forEach(function (fn) {
        try { fn(); } catch (e) {}
      });
    };
    document.head.appendChild(script);
  }

  class VideoTimestampsElement extends HTMLElement {
    constructor() {
      super();
      this.videoFrame = null;
      this.videoProvider = null;
      this.player = null;
      this.isReady = false;
      this.pendingSeek = null;
      this._timer = null;
      this.durationEl = null;
      this.durationManual = false;
    }

    connectedCallback() {
      this.videoFrame = this.querySelector('[data-video-provider]');
      this.timestampItems = this.querySelectorAll("[data-timestamp]");

      this.durationEl = this.querySelector("[data-duration-output]");
      this.durationManual = this.durationEl ? (this.durationEl.getAttribute("data-duration-manual") === "1") : false;

      if (this.videoFrame) {
        this.videoProvider = this.videoFrame.getAttribute("data-video-provider");
        this.initializePlayer();
      }

      this.setupTimestampListeners();
    }

    disconnectedCallback() {
      if (this._timer) clearInterval(this._timer);
    }

    initializePlayer() {
      if (this.videoProvider === "youtube") {
        loadYouTubeAPI(() => this.createYouTubePlayer());
      } else if (this.videoProvider === "vimeo") {
        loadVimeoAPI(() => this.createVimeoPlayer());
      }
    }

    createYouTubePlayer() {
      if (!window.YT || !window.YT.Player) return;

      this.player = new YT.Player(this.videoFrame, {
        events: {
          onReady: () => {
            this.isReady = true;

            // Duration (auto) — only if manual text is blank
            this.trySetDurationFromPlayer();

            if (this.pendingSeek != null) {
              this.player.seekTo(this.pendingSeek, true);
              this.player.playVideo();
              this.pendingSeek = null;
            }
            this.setupTimeTracking();
          }
        }
      });
    }

    createVimeoPlayer() {
      if (!window.Vimeo || !window.Vimeo.Player) return;

      this.player = new Vimeo.Player(this.videoFrame);
      this.player.ready().then(() => {
        this.isReady = true;

        // Duration (auto) — only if manual text is blank
        this.trySetDurationFromPlayer();

        if (this.pendingSeek != null) {
          this.player.setCurrentTime(this.pendingSeek);
          this.player.play();
          this.pendingSeek = null;
        }
        this.setupTimeTracking();
      }).catch(() => {});
    }

    setupTimeTracking() {
      if (this._timer) clearInterval(this._timer);

      if (this.videoProvider === "youtube" && this.player) {
        this._timer = setInterval(() => {
          try {
            if (this.player.getPlayerState && this.player.getPlayerState() === 1) {
              const currentTime = this.player.getCurrentTime();
              if (typeof currentTime === "number") this.updateActiveTimestamp(currentTime);
            }
          } catch (e) {}
        }, 500);
      } else if (this.videoProvider === "vimeo" && this.player) {
        this.player.on("timeupdate", (data) => {
          this.updateActiveTimestamp(data.seconds);
        });
      }
    }

    updateActiveTimestamp(currentTime) {
      let activeItem = null;
      this.timestampItems.forEach((item) => {
        const seconds = this.parseTimestamp(item.getAttribute("data-timestamp"));
        if (!isNaN(seconds) && currentTime >= seconds) activeItem = item;
      });

      this.timestampItems.forEach((item) => item.classList.remove("active"));
      if (activeItem) activeItem.classList.add("active");
    }

    setupTimestampListeners() {
      this.timestampItems.forEach((item) => {
        item.addEventListener("click", () => this.handleTimestampClick(item));
        item.addEventListener("keydown", (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            this.handleTimestampClick(item);
          }
        });
      });
    }

    handleTimestampClick(item) {
      const seconds = this.parseTimestamp(item.getAttribute("data-timestamp"));
      if (isNaN(seconds)) return;

      if (!this.player || !this.isReady) {
        this.pendingSeek = seconds;
        this.timestampItems.forEach((i) => i.classList.remove("active"));
        item.classList.add("active");
        return;
      }

      if (this.videoProvider === "youtube" && this.player.seekTo) {
        this.player.seekTo(seconds, true);
        this.player.playVideo();
      } else if (this.videoProvider === "vimeo") {
        this.player.setCurrentTime(seconds).then(() => {
          this.player.play();
        }).catch(() => {});
      }

      this.timestampItems.forEach((i) => i.classList.remove("active"));
      item.classList.add("active");
    }

    parseTimestamp(timestamp) {
      const parts = String(timestamp || "").split(":").reverse();
      let seconds = 0;
      parts.forEach((part, index) => {
        const n = parseInt(part, 10);
        if (!isNaN(n)) seconds += n * Math.pow(60, index);
      });
      return seconds;
    }

    // -------- Duration helpers --------
    trySetDurationFromPlayer() {
      if (!this.durationEl) return;
      if (this.durationManual) return; // merchant override

      const prefix = (this.getAttribute("data-duration-prefix") || "Duration").trim();
      const prefixText = prefix ? (prefix + ": ") : "";

      if (this.videoProvider === "youtube" && this.player && this.player.getDuration) {
        // YouTube duration may be 0 until metadata is ready; retry a few times
        let tries = 0;
        const tick = () => {
          tries++;
          let d = 0;
          try { d = this.player.getDuration(); } catch (e) {}
          if (typeof d === "number" && d > 0) {
            this.durationEl.textContent = prefixText + this.formatDuration(d);
          } else if (tries < 20) {
            setTimeout(tick, 250);
          }
        };
        tick();
      }

      if (this.videoProvider === "vimeo" && this.player && this.player.getDuration) {
        this.player.getDuration().then((d) => {
          if (typeof d === "number" && d > 0) {
            this.durationEl.textContent = prefixText + this.formatDuration(d);
          }
        }).catch(() => {});
      }
    }

    formatDuration(totalSeconds) {
      const s = Math.max(0, Math.round(totalSeconds));
      const h = Math.floor(s / 3600);
      const m = Math.floor((s % 3600) / 60);
      const r = s % 60;

      if (h > 0) {
        return `${h}:${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
      }
      return `${m}:${String(r).padStart(2, "0")}`;
    }
  }

  if (!window.__videoTimestampsBaseDefined) {
    window.__videoTimestampsBaseDefined = true;

    function defineForTag(el) {
      if (!el || !el.tagName) return;
      var name = el.tagName.toLowerCase();
      if (name.startsWith("video-timestamps-") && !customElements.get(name)) {
        try { customElements.define(name, VideoTimestampsElement); } catch (e) {}
      }
    }

    // Define for existing custom elements
    document.querySelectorAll('*').forEach(defineForTag);

    // Observe for dynamically added elements
    var mo = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.addedNodes && m.addedNodes.forEach(function (node) {
          if (!node || node.nodeType !== 1) return;
          defineForTag(node);
          if (node.querySelectorAll) node.querySelectorAll('*').forEach(defineForTag);
        });
      });
    });

    mo.observe(document.documentElement, { childList: true, subtree: true });
  }
})();
